import React, { useEffect, useRef, useLayoutEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BriefcaseIcon, HomeIcon, SuccessEditConfirmationIcon } from "../../../../../asset-controller-mod/asset-serv/icons/iconExporter";
import "../../css/hire-create.css";
import MainBreadcrumb from "../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb";
import SelectField from "../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField";
import TextField from "../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField";
import CmsEditor from "../../../../../component-controller-mod/component-serv/texteditor-fs-cp/tip-tap-editor/ts/cms-editor";
import { Button, Confirmation, Empty } from "@core/uilibrary";
import { useGlobalPopupProvider } from "../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider";
import { useToast } from "../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider";

import {
  CareerStatus,
  CreateJobBody,
  DepartmentItem,
  FieldErrors,
  getDepartmentIndexApi,
  getJobDetailApi,
  getJobLevelIndexApi,
  getJobTypeIndexApi,
  JobDetailItem,
  JobLevelItem,
  JobTypeItem,
  updateJobApi,
} from "./hire.type";

type Option = { value: string; label: string };

const toOptions = <T,>(
  items: T[],
  getId: (x: T) => string,
  getLabel: (x: T) => string
): Option[] => [{ value: "all", label: "All" }, ...items.map((x) => ({ value: getId(x), label: getLabel(x) }))];

type RichBlock = { desc: string; html: string };
const safeParseRich = (
  raw?: string | null,
  keys?: { desc: string; html: string }
): RichBlock => {
  const k = keys ?? { desc: "responsibleDescription", html: "responsibleHtml" };
  if (!raw) return { desc: "", html: "" };

  try {
    const obj = JSON.parse(raw);
    return {
      desc: typeof obj?.[k.desc] === "string" ? obj[k.desc] : "",
      html: typeof obj?.[k.html] === "string" ? obj[k.html] : "",
    };
  } catch {
    return { desc: "", html: "" };
  }
};

const findUuidByName = (options: Option[], name?: string | null) => {
  if (!name) return null;
  const hit = options.find((o) => o.label?.trim().toLowerCase() === name.trim().toLowerCase());
  return hit?.value && hit.value !== "all" ? hit.value : null;
};

const HireEdit = () => {
  const navigate = useNavigate();
  const { careerId } = useParams<{ careerId: string }>(); // you use careerId in routes; it’s jobUuid
  const jobUuid = careerId;

  const { openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [errors, setErrors] = useState<FieldErrors>({});
  const inputsSectionRef = useRef<HTMLElement | null>(null);

  // dropdown options
  const [jobLevelOptions, setJobLevelOptions] = useState<Option[]>([]);
  const [jobTypeOptions, setJobTypeOptions] = useState<Option[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);

  // selects (uuids)
  const [jobLevelUuid, setJobLevelUuid] = useState<string | null>(null);
  const [jobTypeUuid, setJobTypeUuid] = useState<string | null>(null);
  const [departmentUuid, setDepartmentUuid] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");

  // fields
  const [jobCode, setJobCode] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");

  // rich fields (desc + html)
  const [jobResponsibleText, setJobResponsibleText] = useState("");
  const [jobResponsibleHtml, setJobResponsibleHtml] = useState("");

  const [skillRequirementText, setSkillRequirementText] = useState("");
  const [skillRequirementHtml, setSkillRequirementHtml] = useState("");

  const [qualificationText, setQualificationText] = useState("");
  const [qualificationHtml, setQualificationHtml] = useState("");

  const [whatWeOfferText, setWhatWeOfferText] = useState("");
  const [whatWeOfferHtml, setWhatWeOfferHtml] = useState("");

  // auto-resize (example for one textarea)
  const responsibleRef = useRef<HTMLTextAreaElement | null>(null);
  const resizeResponsible = () => {
    const el = responsibleRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useLayoutEffect(() => {
    resizeResponsible();
  }, [jobResponsibleText]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!jobUuid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // 1) load dropdown data + detail in parallel
        const [lvlRes, typeRes, depRes, detailRes] = await Promise.all([
          getJobLevelIndexApi(),
          getJobTypeIndexApi(),
          getDepartmentIndexApi(),
          getJobDetailApi(jobUuid),
        ]);
        if (!alive) return;

        // options
        const lvlItems: JobLevelItem[] = lvlRes?.data?.data?.data?.items ?? [];
        const typeItems: JobTypeItem[] = typeRes?.data?.data?.items ?? [];
        const depItems: DepartmentItem[] = depRes?.data?.data?.items ?? [];

        const lvlOpts = toOptions(lvlItems, (x) => x.uuid, (x) => x.name);
        const typeOpts = toOptions(typeItems, (x) => x.uuid, (x) => x.name);
        const depOpts = toOptions(depItems, (x) => x.departmentUuid, (x) => x.name);

        setJobLevelOptions(lvlOpts);
        setJobTypeOptions(typeOpts);
        setDepartmentOptions(depOpts);

        // detail
        const job: JobDetailItem | null = detailRes?.data?.data ?? null;
        if (!job) return;

        setJobCode(job.jobCode ?? "");
        setJobTitle(job.jobTitle ?? "");
        setJobDescription(job.description ?? "");
        setStatus(job.status ?? "");

        // parse rich json fields
        const resp = safeParseRich(job.responsibilities, {
          desc: "responsibleDescription",
          html: "responsibleHtml",
        });
        setJobResponsibleText(resp.desc);
        setJobResponsibleHtml(resp.html);

        const req = safeParseRich(job.requirements, {
          desc: "requirementsDescription",
          html: "requirementsHtml",
        });
        setSkillRequirementText(req.desc);
        setSkillRequirementHtml(req.html);

        const qual = safeParseRich(job.qualification, {
          desc: "qualificationDescription",
          html: "qualificationHtml",
        });
        setQualificationText(qual.desc);
        setQualificationHtml(qual.html);

        const offer = safeParseRich(job.offer, {
          desc: "offerDescription",
          html: "offerHtml",
        });
        setWhatWeOfferText(offer.desc);
        setWhatWeOfferHtml(offer.html);

        // map NAME -> UUID (because detail doesn’t provide IDs)
        setDepartmentUuid(findUuidByName(depOpts, job.departmentName));
        setJobLevelUuid(findUuidByName(lvlOpts, job.jobLevelName));
        setJobTypeUuid(findUuidByName(typeOpts, job.jobType));

      } catch (e) {
        console.error("Load edit data failed:", e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [jobUuid]);

  const handleUpdate = () => {
    const nextErrors: FieldErrors = {};

    if (!jobCode.trim()) nextErrors.jobCode = "x";
    if (!jobTitle.trim()) nextErrors.jobTitle = "x";
    if (!jobLevelUuid) nextErrors.jobLevel = "x";
    if (!jobTypeUuid) nextErrors.jobType = "x";
    if (!departmentUuid) nextErrors.department = "x";
    if (!status) nextErrors.status = "x";

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      inputsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const payload: CreateJobBody = {
      jobTitle,
      status,
      departmentId: departmentUuid!,
      jobLevelId: jobLevelUuid!,
      jobTypeId: jobTypeUuid!,
      description: jobDescription,

      responsibilities: JSON.stringify({
        responsibleDescription: jobResponsibleText,
        responsibleHtml: jobResponsibleHtml,
      }),
      requirements: JSON.stringify({
        requirementsDescription: skillRequirementText,
        requirementsHtml: skillRequirementHtml,
      }),
      qualification: JSON.stringify({
        qualificationDescription: qualificationText,
        qualificationHtml: qualificationHtml,
      }),
      offer: JSON.stringify({
        offerDescription: whatWeOfferText,
        offerHtml: whatWeOfferHtml,
      }),
    };

    openConfirm(
      <Confirmation
        visible
        title="Update job?"
        description="Are you sure you want to update this job?"
        status="info"
        onClose={closeConfirm}
        icon={<SuccessEditConfirmationIcon/>}
        buttons={
          <>
            <Button
              variant="ghost"
              size="md"
              value="Cancel"
              type="button"
              disabled={saving}
              onClick={closeConfirm}
            />
            <Button
              size="md"
              value={saving ? "Saving..." : "Confirm"}
              type="button"
              disabled={saving}
              onClick={async () => {
                if (!jobUuid) return;
                try {
                  setSaving(true);
                  await updateJobApi(jobUuid, payload);

                  showToast({
                    status: "success",
                    title: "Updated",
                    description: "Job has been updated successfully.",
                  });

                  closeConfirm();
                  navigate(`/career/hire/${jobUuid}/view`); // or /career/hire
                } catch (e) { //NOSONAR
                  showToast({
                    status: "error",
                    title: "Failed",
                    description: "Job cannot be updated.",
                  });
                } finally {
                  setSaving(false);
                }
              }}
            />
          </>
        }
      />
    );
  };

  if (!jobUuid) {
    return (
      <main className="scl--crr-add-const-styl-main">
        <Empty title="Missing job id" description="No jobUuid provided in the URL." />
      </main>
    );
  }

  if (loading) {
    return (
      <main className="scl--crr-add-const-styl-main">
        <Empty title="Loading..." description="Fetching job details." />
      </main>
    );
  }

  return (
    <main className="scl--crr-add-const-styl-main">
      <section ref={inputsSectionRef} className="scl--crr-add-const-bread-sec-on">
        <button onClick={() => navigate("/career/hire")}>
          <HomeIcon />
          <span className='span-bread'>
            Hiring
          </span>
        </button>
        <span>/</span>
        <h6>
          <BriefcaseIcon />
          <span className="span-bread">Edit</span>
        </h6>
      </section>

      <section className="scl--crr-add-const-title-sec-tw">
        <MainBreadcrumb
          title="Edit job"
          paragraph="Update job information and publish workflow fields."
        />
      </section>

      <section className="scl--crr-const-inp-sty-box">
        <div className="grid-item">
          <TextField
            name="jobcode"
            label="Job code"
            placeholder="Enter job code"
            value={jobCode}
            onChange={(v) => {
              setJobCode(v);
              if (v.trim()) setErrors((p) => ({ ...p, jobCode: undefined }));
            }}
            required
            error={!!errors.jobCode}
            disabled
          />
        </div>

        <div className="grid-item">
          <TextField
            name="jobtitle"
            label="Job Title"
            placeholder="Enter job title"
            value={jobTitle}
            onChange={(v) => {
              setJobTitle(v);
              if (v.trim()) setErrors((p) => ({ ...p, jobTitle: undefined }));
            }}
            required
            error={!!errors.jobTitle}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="jobLevel"
            placeholder="Job level"
            label="Job level"
            options={jobLevelOptions}
            value={jobLevelUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setJobLevelUuid(val);
              setErrors((p) => ({ ...p, jobLevel: undefined }));
            }}
            required
            error={!!errors.jobLevel}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="jobType"
            placeholder="Job type"
            label="Job type"
            options={jobTypeOptions}
            value={jobTypeUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setJobTypeUuid(val);
              setErrors((p) => ({ ...p, jobType: undefined }));
            }}
            required
            error={!!errors.jobType}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="department"
            placeholder="Department"
            label="Department"
            options={departmentOptions}
            value={departmentUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setDepartmentUuid(val);
              setErrors((p) => ({ ...p, department: undefined }));
            }}
            required
            error={!!errors.department}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="status"
            placeholder="status"
            label="status"
            options={CareerStatus}
            value={status}
            onChange={(v) => {
              setStatus(String(v));
              setErrors((p) => ({ ...p, status: undefined }));
            }}
            required
            error={!!errors.status}
          />
        </div>
      </section>

      <section className="scl--crr-deta-jb-add-sty-box">
        <div className="scl--crr-dja-sty-box-item">
          <h6>Job Description</h6>
          <textarea
            className="text-area-fst-jb"
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
          />
        </div>

        <div className="scl--crr-dja-sty-box-item">
          <h6>Job responsible</h6>
          <textarea
            ref={responsibleRef}
            className="text-area-ota"
            value={jobResponsibleText}
            onChange={(e) => setJobResponsibleText(e.target.value)}
          />
          <CmsEditor value={jobResponsibleHtml} onChange={setJobResponsibleHtml} minHeight={92} />
        </div>

        <div className="scl--crr-dja-sty-box-item">
          <h6>Skill requirement</h6>
          <textarea
            className="text-area-ota"
            value={skillRequirementText}
            onChange={(e) => setSkillRequirementText(e.target.value)}
          />
          <CmsEditor value={skillRequirementHtml} onChange={setSkillRequirementHtml} minHeight={92} />
        </div>

        <div className="scl--crr-dja-sty-box-item">
          <h6>Qualification</h6>
          <textarea
            className="text-area-ota"
            value={qualificationText}
            onChange={(e) => setQualificationText(e.target.value)}
          />
          <CmsEditor value={qualificationHtml} onChange={setQualificationHtml} minHeight={92} />
        </div>

        <div className="scl--crr-dja-sty-box-item">
          <h6>What we offer</h6>
          <textarea
            className="text-area-ota"
            value={whatWeOfferText}
            onChange={(e) => setWhatWeOfferText(e.target.value)}
          />
          <CmsEditor value={whatWeOfferHtml} onChange={setWhatWeOfferHtml} minHeight={92} />
        </div>
      </section>

      <section className="scl--crr-btn-add-sty-box">
        <Button variant="ghost" value="Cancel" type="button" onClick={() => navigate("/career/hire")} />
        <Button value="Update" type="button" onClick={handleUpdate} disabled={saving} />
      </section>
    </main>
  );
};

export default HireEdit;